import { useState, type JSX } from 'react';
import {
  hoje,
  mesmoPeriodo,
  periodoIncompleto,
  periodoInvertido,
  ultimosDias,
  type Periodo,
} from '../lib/periodo.js';
import './SeletorPeriodo.css';

/**
 * Seletor de período — Frente F, Story F.2.
 *
 * Atalhos (Hoje · 7 dias · 30 dias) mais um par de campos de data. Nasce aqui,
 * e não dentro da lista de orçamentos, porque a **página de logs (L.4)** vai
 * pedir exatamente o mesmo controle — e um seletor de data reescrito é um lugar
 * a mais para "7 dias" significar coisa diferente.
 *
 * ## O componente não busca nada
 *
 * Ele avisa `aoMudar` e para por aí. Quem decide o que fazer com o período é a
 * página: a lista de orçamentos refaz a query, e a de logs fará o que for dela.
 * Um seletor que soubesse chamar a API amarraria as duas telas ao mesmo
 * endpoint.
 *
 * ## Os atalhos leem o relógio NO CLIQUE
 *
 * `hoje(new Date())` acontece dentro do `onClick`, e não num `useMemo` do
 * primeiro render. É o defeito que a revisão do dashboard encontrou: numa aba
 * deixada aberta — que é como uma lista de trabalho vive —, um atalho congelado
 * passa a devolver o período de ontem, e o botão continua destacado dizendo que
 * é hoje.
 */

export interface AtalhoPeriodo {
  rotulo: string;
  /** Recebe o `agora` do clique. */
  calcular: (agora: Date) => Periodo;
}

/**
 * Os atalhos padrão. `Hoje · 7 dias · 30 dias`, na ordem do menor para o maior,
 * que é a ordem em que se procura ("o que fiz hoje" antes de "o que fiz no
 * mês").
 */
export const ATALHOS_PADRAO: readonly AtalhoPeriodo[] = [
  { rotulo: 'Hoje', calcular: (a) => hoje(a) },
  { rotulo: '7 dias', calcular: (a) => ultimosDias(a, 7) },
  { rotulo: '30 dias', calcular: (a) => ultimosDias(a, 30) },
];

export function SeletorPeriodo({
  periodo,
  aoMudar,
  atalhos = ATALHOS_PADRAO,
  rotuloGrupo = 'Período',
}: {
  periodo: Periodo;
  aoMudar: (p: Periodo) => void;
  atalhos?: readonly AtalhoPeriodo[];
  rotuloGrupo?: string;
}): JSX.Element {
  /*
   * "Personalizado" só controla a VISIBILIDADE dos campos — o período em si
   * mora na página. Se este estado guardasse datas, haveria duas fontes para o
   * mesmo valor, e elas se separariam no primeiro atalho clicado.
   *
   * Começa aberto quando o período em vigor não é nenhum dos atalhos: é o caso
   * de quem recarrega a página com um intervalo escolhido à mão, e fechar os
   * campos esconderia justamente o que está valendo.
   */
  const [personalizado, setPersonalizado] = useState(
    () => !atalhos.some((a) => mesmoPeriodo(periodo, a.calcular(new Date()))),
  );

  const invertido = periodoInvertido(periodo);
  const incompleto = periodoIncompleto(periodo);

  return (
    <div className="periodo">
      <div className="periodo__atalhos" role="group" aria-label={rotuloGrupo}>
        {atalhos.map((a) => {
          const ativo = !personalizado && mesmoPeriodo(periodo, a.calcular(new Date()));
          return (
            <button
              key={a.rotulo}
              type="button"
              /* Sem `aria-pressed` os botões soam idênticos num leitor de tela:
                 "Hoje, botão" / "7 dias, botão", sem dizer qual está valendo. */
              aria-pressed={ativo}
              className={ativo ? 'botao botao--primario' : 'botao botao--neutro'}
              onClick={() => {
                setPersonalizado(false);
                aoMudar(a.calcular(new Date()));
              }}
            >
              {a.rotulo}
            </button>
          );
        })}
        <button
          type="button"
          aria-pressed={personalizado}
          className={personalizado ? 'botao botao--primario' : 'botao botao--neutro'}
          onClick={() => {
            setPersonalizado((p) => !p);
          }}
        >
          Personalizado
        </button>
      </div>

      {personalizado && (
        <div className="periodo__datas">
          <label className="campo periodo__campo">
            <span className="campo__rotulo">De</span>
            <input
              type="date"
              className="campo__entrada"
              value={periodo.de}
              onChange={(e) => {
                aoMudar({ ...periodo, de: e.target.value });
              }}
            />
          </label>
          <label className="campo periodo__campo">
            <span className="campo__rotulo">Até</span>
            <input
              type="date"
              className="campo__entrada"
              value={periodo.ate}
              onChange={(e) => {
                aoMudar({ ...periodo, ate: e.target.value });
              }}
            />
          </label>
        </div>
      )}

      {/*
        O aviso mora AQUI, junto dos campos que o causaram, e não no lugar da
        lista. Quem está mexendo nas datas está olhando para cá.
      */}
      {incompleto && (
        <p className="aviso periodo__aviso" role="alert">
          Escolha as duas datas do período.
        </p>
      )}
      {!incompleto && invertido && (
        <p className="aviso periodo__aviso" role="alert">
          A data inicial ({periodo.de}) é depois da final ({periodo.ate}).
        </p>
      )}
    </div>
  );
}
